import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Box, Card, CardContent, Typography, ToggleButtonGroup, ToggleButton } from "@mui/material";
import ShowChartIcon from "@mui/icons-material/ShowChart";
import BarChartIcon from "@mui/icons-material/BarChart";
import Headeer from "../../components/Headeer";
import StatsFilters from "../../components/Stats/StatsFilters";
import StatsChart from "../../components/Stats/StatsChart";
import StatsSummaryCards from "../../components/Stats/StatsSummaryCards";
import StatsService from "../../services/doctorServices/StatsService";
import FileService from "../../services/doctorServices/FileService";
import { tokens } from "../../theme";
import { useTheme } from "@emotion/react";

const PATIENTS_PAGE_ENTITIES = [{ value: "patients", label: "Patients" }];

const PATIENTS_PAGE_METRICS = {
  patients: [
    { value: "count", label: "Total Count" },
    { value: "new_count", label: "New Patient Count" },
    { value: "by_gender", label: "By Gender" },
    { value: "by_age_group", label: "By Age Group" },
    { value: "by_file", label: "Patients per File" },
  ],
};

function getPatientsPageMetricsForEntity(entity) {
  return PATIENTS_PAGE_METRICS[entity] || PATIENTS_PAGE_METRICS.patients;
}

export default function PatientsStatistics() {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { t } = useTranslation("patientsPage");
  const fileService = useMemo(() => new FileService(), []);
  const statsService = useMemo(() => new StatsService(), []);

  const [files, setFiles] = useState([]);
  const [statsData, setStatsData] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [statsError, setStatsError] = useState(null);
  const [selectedEntity, setSelectedEntity] = useState("patients");
  const [selectedMetric, setSelectedMetric] = useState("count");
  const [selectedTimeFilter, setSelectedTimeFilter] = useState("last_month");
  const [selectedGroupBy, setSelectedGroupBy] = useState("day");
  const [customStartDate, setCustomStartDate] = useState("");
  const [customEndDate, setCustomEndDate] = useState("");
  const [chartView, setChartView] = useState("line"); // "line" | "bar"

  useEffect(() => {
    const loadFiles = async () => {
      try {
        const res = await fileService.getAllFiles();
        const list = Array.isArray(res) ? res : (res?.files || []);
        setFiles(list);
      } catch (e) {
        setFiles([]);
      }
    };
    loadFiles();
  }, [fileService]);

  useEffect(() => {
    const firstMetric = getPatientsPageMetricsForEntity(selectedEntity)[0]?.value || "count";
    setSelectedMetric(firstMetric);
  }, [selectedEntity]);

  const fetchStatsData = useCallback(async () => {
    setStatsLoading(true);
    setStatsError(null);
    try {
      let startDate, endDate;
      if (selectedTimeFilter === "custom") {
        startDate = customStartDate || "";
        endDate = customEndDate || "";
        if (!startDate || !endDate) {
          setStatsError("Please select both start and end dates for custom date range");
          setStatsLoading(false);
          setStatsData(null);
          return;
        }
        if (new Date(endDate) < new Date(startDate)) {
          setStatsError("End date must be after start date");
          setStatsLoading(false);
          setStatsData(null);
          return;
        }
      } else {
        const range = statsService.getDateRange(selectedTimeFilter);
        startDate = range.startDate;
        endDate = range.endDate;
      }
      // by_gender, by_age_group, by_file: zaman serisi (kümülatif) için group_by her zaman gün/hafta/ay olmalı
      const groupBy =
        selectedGroupBy === "none"
          ? statsService.getSuggestedGroupBy(selectedTimeFilter)
          : selectedGroupBy;
      const params = {
        entity: selectedEntity,
        metric: selectedMetric,
        start_date: startDate,
        end_date: endDate,
        group_by: groupBy,
      };
      const response = await statsService.getDoctorStats(params);
      if (response && response.data) {
        setStatsData(response);
      } else {
        throw new Error("Invalid response from server");
      }
    } catch (error) {
      console.error("Patients stats error:", error);
      setStatsError(error.response?.data?.message || error.message || "Failed to load stats");
      setStatsData(null);
    } finally {
      setStatsLoading(false);
    }
  }, [
    selectedEntity,
    selectedMetric,
    selectedTimeFilter,
    selectedGroupBy,
    customStartDate,
    customEndDate,
    statsService,
  ]);

  useEffect(() => {
    if (selectedTimeFilter !== "custom" || (customStartDate && customEndDate)) {
      fetchStatsData();
    }
  }, [fetchStatsData, selectedTimeFilter, customStartDate, customEndDate]);

  return (
    <Box sx={{ p: 3 }}>
      <Headeer title={t("statisticsTitle")} subtitle={t("statisticsSubtitle")} />

      <Box sx={{ mb: 4, mt: 3 }}>
        <StatsFilters
          entity={selectedEntity}
          metric={selectedMetric}
          timeFilter={selectedTimeFilter}
          groupBy={selectedGroupBy}
          onEntityChange={setSelectedEntity}
          onMetricChange={setSelectedMetric}
          onTimeFilterChange={(value) => {
            setSelectedTimeFilter(value);
            if (value !== "custom") {
              setCustomStartDate("");
              setCustomEndDate("");
            }
            setSelectedGroupBy(statsService.getSuggestedGroupBy(value));
          }}
          onGroupByChange={setSelectedGroupBy}
          onRefresh={fetchStatsData}
          customStartDate={customStartDate}
          customEndDate={customEndDate}
          onCustomStartDateChange={setCustomStartDate}
          onCustomEndDateChange={setCustomEndDate}
          entities={PATIENTS_PAGE_ENTITIES}
          getMetricsForEntity={getPatientsPageMetricsForEntity}
        />
        {statsData?.data?.[selectedEntity]?.summary && (
          <StatsSummaryCards
            summary={statsData.data[selectedEntity].summary}
            comparison={statsData.data[selectedEntity].comparison}
            entity={selectedEntity}
            metric={selectedMetric}
          />
        )}
        <Card
          sx={{
            background: `linear-gradient(135deg, ${colors.primary[400]} 0%, ${colors.primary[500]} 100%)`,
            borderRadius: "16px",
            border: `1px solid ${colors.grey[800]}30`,
            mt: 2,
          }}
        >
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 2, mb: 3 }}>
              <Typography variant="h5" sx={{ fontWeight: 700, color: colors.grey[100] }}>
                {t("stats:entities.patients") || "Patients"} –{" "}
                {t(`stats:metrics.${selectedMetric}`) || selectedMetric.replace(/_/g, " ")}
              </Typography>
              <ToggleButtonGroup
                value={chartView}
                exclusive
                onChange={(_, newView) => {
                  if (newView != null) setChartView(newView);
                }}
                size="small"
                sx={{
                  "& .MuiToggleButton-root": {
                    color: colors.grey[300],
                    borderColor: colors.grey[700],
                    "&.Mui-selected": {
                      backgroundColor: colors.primary[500],
                      color: colors.grey[100],
                      "&:hover": { backgroundColor: colors.primary[600] },
                    },
                  },
                }}
              >
                <ToggleButton value="line" aria-label={t("chartLine")}>
                  <ShowChartIcon sx={{ mr: 0.5 }} />
                  {t("chartLine")}
                </ToggleButton>
                <ToggleButton value="bar" aria-label={t("chartBar")}>
                  <BarChartIcon sx={{ mr: 0.5 }} />
                  {t("chartBar")}
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
            <StatsChart
              data={statsData?.data}
              loading={statsLoading}
              error={statsError}
              metric={selectedMetric}
              entity={selectedEntity}
              files={files}
              period={selectedTimeFilter}
              groupBy={
                selectedGroupBy === "none"
                  ? statsService.getSuggestedGroupBy(selectedTimeFilter)
                  : selectedGroupBy
              }
              preferredChartType={chartView}
            />
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
}
